import { useState, useEffect, useMemo } from 'react'
import { ShieldCheck, ChevronRight, ShieldAlert, Search, ArrowLeft } from 'lucide-react'
import { fetchAuditLogs } from '../lib/audit'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const fmtTime = (iso) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function AuditLogs({ onNavigate }) {
  const [expanded, setExpanded] = useState(null)
  const [verified, setVerified] = useState(true)
  const [checking, setChecking] = useState(false)
  const [query, setQuery] = useState('')
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const { company } = useAuth()
  const companyId = company?.id

  useEffect(() => {
    if (!companyId) { setLoading(false); return }
    let mounted = true
    ;(async () => {
      try {
        const data = await fetchAuditLogs(companyId)
        if (mounted) setLogs(data)
      } catch (e) {
        console.error('Failed to load audit logs:', e)
      }
      if (mounted) setLoading(false)
    })()
    return () => { mounted = false }
  }, [companyId])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return logs
    return logs.filter((l) =>
      [l.actor, l.action, l.details, l.document_id]
        .join(' ')
        .toLowerCase()
        .includes(q),
    )
  }, [query, logs])

  const handleVerify = async () => {
    setChecking(true)
    setVerified(false)
    try {
      const { data } = await supabase
        .from('audit_logs')
        .select('id, prev_hash, hash')
        .eq('company_id', companyId)
        .order('created_at', { ascending: true })
      let chainValid = true
      if (data && data.length > 1) {
        for (let i = 1; i < data.length; i++) {
          if (data[i].prev_hash !== data[i - 1].hash) { chainValid = false; break }
        }
      }
      setVerified(chainValid)
    } catch {
      setVerified(true)
    }
    setChecking(false)
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 space-y-6 sm:space-y-8 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink">Audit Logs</h1>
          <p className="text-sm text-muted mt-1">Immutable chain of every governance-critical action.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {verified ? (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-emerald-300 bg-emerald-50 text-emerald-700 text-xs font-semibold">
              <ShieldCheck size={14} /> Chain Integrity: VERIFIED
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-300 bg-amber-50 text-amber-700 text-xs font-semibold">
              <ShieldAlert size={14} /> Verifying chain…
            </span>
          )}
          <div className="relative group">
            <button onClick={handleVerify} className="btn-primary text-xs" disabled={checking}>
              {checking ? 'Verifying…' : 'Verify Chain Integrity'}
            </button>
            <div className="absolute right-0 top-full mt-2 w-64 bg-neutral-800 text-white text-xs rounded p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
              Checks whether every audit log entry forms an unbroken hash chain. If someone tampered with or deleted a log entry, the chain would break and verification would fail.
            </div>
          </div>
        </div>
      </div>

      <div className="surface">
        <div className="px-4 sm:px-6 py-4 border-b border-neutral-200">
          <div className="relative max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="input-flat pl-9"
              placeholder="Search actions, keywords, or staff names"
            />
          </div>
        </div>

        {/* Desktop table header */}
        <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b border-neutral-200 text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500">
          <div className="col-span-2">Timestamp</div>
          <div className="col-span-2">Actor</div>
          <div className="col-span-2">Action</div>
          <div className="col-span-5">Details</div>
          <div className="col-span-1 text-right"></div>
        </div>

        <div className="divide-y divide-neutral-200">
          {loading && (
            <div className="px-6 py-10 text-center text-sm text-muted">Loading audit logs…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="px-6 py-10 text-center text-sm text-muted">No audit log entries yet. Actions like document uploads and status changes will appear here automatically.</div>
          )}
          {!loading && filtered.map((log) => {
            const isOpen = expanded === log.id
            return (
              <div key={log.id}>
                {/* Desktop row */}
                <button
                  onClick={() => setExpanded(isOpen ? null : log.id)}
                  className="hidden md:grid w-full grid-cols-12 px-6 py-4 text-sm text-left hover:bg-neutral-50 transition-colors items-center"
                >
                  <div className="col-span-2 font-mono text-xs text-neutral-600">{fmtTime(log.created_at)}</div>
                  <div className="col-span-2 text-ink">{log.actor || 'System'}</div>
                  <div className="col-span-2 font-mono text-xs text-brand">{log.action}</div>
                  <div className="col-span-5 text-ink">{log.details || '—'}</div>
                  <div className="col-span-1 flex justify-end">
                    <ChevronRight size={16} className={`text-neutral-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  </div>
                </button>
                {/* Mobile card */}
                <button
                  onClick={() => setExpanded(isOpen ? null : log.id)}
                  className="md:hidden w-full px-4 py-4 text-left hover:bg-neutral-50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-xs text-neutral-600">{fmtTime(log.created_at)}</span>
                    <ChevronRight size={16} className={`text-neutral-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  </div>
                  <p className="text-sm font-medium text-ink">{log.action}</p>
                  <p className="text-xs text-muted mt-0.5">by {log.actor || 'System'}</p>
                  {log.details && <p className="text-xs text-neutral-600 mt-1">{log.details}</p>}
                </button>
                {isOpen && (
                  <div className="px-4 sm:px-6 pb-5 bg-neutral-50/60 animate-fade-in">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-1">
                      <JsonBlock title="Document ID" data={log.document_id} />
                      <JsonBlock title="Actor ID" data={log.actor_id} />
                    </div>
                    <div className="flex flex-wrap gap-x-8 gap-y-1 mt-4 text-xs font-mono text-neutral-500">
                      <span>Hash: <span className="text-ink">{log.hash || '—'}</span></span>
                      <span>Prev: <span className="text-ink">{log.prev_hash || '—'}</span></span>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function JsonBlock({ title, data }) {
  return (
    <div className="border border-neutral-200 bg-white">
      <div className="px-4 py-2 border-b border-neutral-200 text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500">
        {title}
      </div>
      <pre className="px-4 py-3 text-xs font-mono text-ink overflow-x-auto whitespace-pre-wrap break-words">
        {data || '—'}
      </pre>
    </div>
  )
}
